/**
 * AuthForm - the single shared sign-in / sign-up form.
 *
 * Reused by the /login and /signup pages AND the AuthModal, so the form + submit
 * logic live in exactly one place. Uses the existing authService + AuthContext
 * (no duplicated auth logic).
 *
 * Props:
 *   initialMode  'login' (default) | 'signup'
 *   showToggle   show the Sign In / Sign Up switch (default true; pages that keep
 *                their own cross-links pass false)
 *   onSuccess    (user) => void  - if provided, called after auth instead of the
 *                default routing (the modal uses this to close + route)
 */

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Box,
  TextField,
  Alert,
  InputAdornment,
  IconButton,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  CircularProgress,
  useTheme,
} from '@mui/material';
import {
  Email,
  Lock,
  Visibility,
  VisibilityOff,
  Person,
  Phone,
  LocationCity,
  ArrowForward,
} from '@mui/icons-material';
import authService from '../../services/authService';
import { useAuth } from '../../context/AuthContext';
import ButtonPill from '../ui/ButtonPill';

const EMPTY = {
  firstName: '',
  lastName: '',
  email: '',
  phone: '',
  city: '',
  password: '',
  confirmPassword: '',
};

const AuthForm = ({ initialMode = 'login', showToggle = true, onSuccess }) => {
  const theme = useTheme();
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();

  const [mode, setMode] = useState(initialMode);
  const [form, setForm] = useState(EMPTY);
  const [showPassword, setShowPassword] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [confirmError, setConfirmError] = useState('');

  const isSignup = mode === 'signup';
  const inputSx = { '& .MuiOutlinedInput-root': { borderRadius: 2 } };

  const handleChange = (e) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    if (error) setError('');
    if (confirmError && e.target.name === 'confirmPassword') setConfirmError('');
  };

  // Default post-auth routing (mirrors the Login page): honour a return-to from
  // ProtectedRoute, the guest-booking hand-off, otherwise go to the dashboard.
  const routeAfterAuth = (user) => {
    const returnTo = location.state?.returnTo;
    const trip = location.state?.trip;
    if (returnTo === '/book' && trip) {
      navigate('/book', { state: { trip } });
      return;
    }
    const isAdminUser = user?.role === 'admin' || user?.role === 'super_admin';
    if (returnTo && returnTo !== '/book' && !returnTo.startsWith('/admin')) {
      navigate(returnTo, { replace: true });
    } else {
      navigate(isAdminUser ? '/admin/dashboard' : '/user/dashboard');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (isSignup && form.password !== form.confirmPassword) {
      setConfirmError('Passwords do not match');
      return;
    }

    try {
      setSubmitting(true);

      if (isSignup) {
        await authService.register({
          email: form.email,
          password: form.password,
          first_name: form.firstName,
          last_name: form.lastName,
          phone: form.phone,
          city: form.city,
        });
      }

      // Registration does not authenticate the client, so sign in either way.
      const response = await authService.login(form.email, form.password);
      login(response.user, response.token);

      if (onSuccess) onSuccess(response.user);
      else routeAfterAuth(response.user);
    } catch (err) {
      const msg = err.error?.message || err.message || 'Something went wrong. Please try again.';
      const list = err.errors || err.error?.errors;
      const detail = Array.isArray(list) ? list.join('. ') : '';
      setError(detail ? `${msg} ${detail}` : msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Box component="form" onSubmit={handleSubmit} noValidate>
      {showToggle && (
        <ToggleButtonGroup
          value={mode}
          exclusive
          fullWidth
          color="primary"
          onChange={(_, val) => {
            if (val) {
              setMode(val);
              setError('');
              setConfirmError('');
            }
          }}
          sx={{ mb: 3 }}
        >
          <ToggleButton value="login" sx={{ fontWeight: 600 }}>Sign In</ToggleButton>
          <ToggleButton value="signup" sx={{ fontWeight: 600 }}>Sign Up</ToggleButton>
        </ToggleButtonGroup>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {isSignup && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="First Name" name="firstName"
                value={form.firstName} onChange={handleChange} sx={inputSx}
                InputProps={{ startAdornment: (<InputAdornment position="start"><Person sx={{ color: theme.palette.primary.main }} /></InputAdornment>) }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Last Name" name="lastName"
                value={form.lastName} onChange={handleChange} sx={inputSx}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12}>
          <TextField
            fullWidth required type="email" label="Email Address" name="email"
            value={form.email} onChange={handleChange} sx={inputSx}
            InputProps={{ startAdornment: (<InputAdornment position="start"><Email sx={{ color: theme.palette.primary.main }} /></InputAdornment>) }}
          />
        </Grid>

        {isSignup && (
          <>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="Phone Number" name="phone"
                value={form.phone} onChange={handleChange} sx={inputSx}
                InputProps={{ startAdornment: (<InputAdornment position="start"><Phone sx={{ color: theme.palette.primary.main }} /></InputAdornment>) }}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth required label="City" name="city"
                value={form.city} onChange={handleChange} sx={inputSx}
                InputProps={{ startAdornment: (<InputAdornment position="start"><LocationCity sx={{ color: theme.palette.primary.main }} /></InputAdornment>) }}
              />
            </Grid>
          </>
        )}

        <Grid item xs={12} sm={isSignup ? 6 : 12}>
          <TextField
            fullWidth required label="Password" name="password"
            type={showPassword ? 'text' : 'password'}
            value={form.password} onChange={handleChange} sx={inputSx}
            InputProps={{
              startAdornment: (<InputAdornment position="start"><Lock sx={{ color: theme.palette.primary.main }} /></InputAdornment>),
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton onClick={() => setShowPassword((s) => !s)} edge="end" aria-label="toggle password visibility">
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />
        </Grid>

        {isSignup && (
          <Grid item xs={12} sm={6}>
            <TextField
              fullWidth required label="Confirm Password" name="confirmPassword"
              type={showPassword ? 'text' : 'password'}
              value={form.confirmPassword} onChange={handleChange}
              error={!!confirmError} helperText={confirmError} sx={inputSx}
              InputProps={{ startAdornment: (<InputAdornment position="start"><Lock sx={{ color: theme.palette.primary.main }} /></InputAdornment>) }}
            />
          </Grid>
        )}
      </Grid>

      <ButtonPill
        type="submit" variant="contained" color="primary" size="large" fullWidth
        disabled={submitting}
        endIcon={submitting ? <CircularProgress size={20} color="inherit" /> : <ArrowForward />}
        sx={{ mt: 3, py: 1.5 }}
      >
        {submitting
          ? (isSignup ? 'Creating Account...' : 'Signing In...')
          : (isSignup ? 'Create Account' : 'Sign In')}
      </ButtonPill>
    </Box>
  );
};

export default AuthForm;
